import React from 'react';
import { getProgressColor, getAchievementStatus, shouldMaskTeamData } from '../utils/teamPerformanceUtils.ts';

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

interface TeamPerformanceTableProps {
  data: any[];
  onSort: (key: string) => void;
  sortConfig: SortConfig | null;
  userRole?: string;
  userTeamName?: string | null;
  getTeamMemberCount: (teamName: string) => number;
}

const TeamPerformanceTable: React.FC<TeamPerformanceTableProps> = React.memo(({
  data,
  onSort,
  sortConfig,
  userRole,
  userTeamName,
  getTeamMemberCount
}) => {
  // 정렬 아이콘 렌더링
  const renderSortIcon = React.useCallback((key: string) => {
    if (!sortConfig || sortConfig.key !== key) {
      return (
        <svg className="w-4 h-4 ml-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    return sortConfig.direction === 'asc' ? (
      <svg className="w-4 h-4 ml-1 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 ml-1 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  }, [sortConfig]);

  // 정렬 가능한 헤더 컴포넌트 (키보드 접근 가능한 button 시맨틱)
  const SortableHeader: React.FC<{ sortKey: string; align?: 'left' | 'right'; children: React.ReactNode }> = ({ sortKey, align = 'left', children }) => (
    <th className="p-0 text-xs font-medium text-gray-700 uppercase tracking-wider">
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`w-full px-4 py-3 flex items-center hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset ${align === 'right' ? 'justify-end' : ''}`}
      >
        {children}
        {renderSortIcon(sortKey)}
      </button>
    </th>
  );

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-2">
        <h2 className="text-lg font-medium text-gray-700">팀별 성과</h2>
        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600">
          {/* 색·용어 범례 — 칩/바/보라의 의미를 색에만 의존하지 않게 명시 */}
          <span className="inline-flex items-center gap-1">
            <span className="inline-block px-1.5 py-0.5 rounded-full font-medium bg-green-100 text-green-800">달성</span>100% 이상
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block px-1.5 py-0.5 rounded-full font-medium bg-yellow-100 text-yellow-800">근접</span>80~100%
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block px-1.5 py-0.5 rounded-full font-medium bg-red-100 text-red-800">미달</span>80% 미만
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="font-medium text-purple-600">보라 금액</span>= 인센티브 산출 대상
          </span>
          <span className="text-gray-600">총 {data.length}개 팀</span>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <SortableHeader sortKey="teamName">팀명</SortableHeader>
              <SortableHeader sortKey="targetSales" align="right">목표매출</SortableHeader>
              <SortableHeader sortKey="actualSales" align="right">실제매출</SortableHeader>
              <SortableHeader sortKey="achievementRate" align="right">달성률</SortableHeader>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                진행률
              </th>
              <SortableHeader sortKey="workingDays" align="right">근무일</SortableHeader>
              <SortableHeader sortKey="dailyAverageSales" align="right">일평균</SortableHeader>
              <SortableHeader sortKey="exceededSales" align="right">초과/미달</SortableHeader>
              <SortableHeader sortKey="deductionAmount" align="right">차감분</SortableHeader>
              <SortableHeader sortKey="incentiveCalculationSales" align="right">인센티브 산출</SortableHeader>
              <SortableHeader sortKey="incentiveAmount" align="right">인센티브</SortableHeader>
            </tr>
          </thead>
          
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map(team => {
              const isMasked = shouldMaskTeamData(team.teamName, userRole, userTeamName);
              
              return (
                <tr key={team.teamName} className="hover:bg-gray-50">
                  {/* 팀명 */}
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    {team.teamName} ({getTeamMemberCount(team.teamName)}명)
                  </td>

                  {/* 목표매출 */}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right tabular-nums text-gray-900">
                    {isMasked ? <span className="text-gray-500">****</span> : `${team.targetSales.toLocaleString()}원`}
                  </td>

                  {/* 실제매출 */}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right tabular-nums text-gray-900">
                    {isMasked ? <span className="text-gray-500">****</span> : `${team.actualSales.toLocaleString()}원`}
                  </td>

                  {/* 달성률: 색+상태어 병기 (삼중 표기) */}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right tabular-nums">
                    {isMasked ? (
                      <span className="text-gray-500">****</span>
                    ) : (
                      <span className="inline-flex items-center justify-end space-x-1.5">
                        <span className="text-gray-900 font-medium">
                          {(team.achievementRate * 100).toFixed(1)}%
                        </span>
                        <span className={`inline-block px-1.5 py-0.5 rounded-full text-xs font-medium ${getAchievementStatus(team.achievementRate).chipClass}`}>
                          {getAchievementStatus(team.achievementRate).label}
                        </span>
                      </span>
                    )}
                  </td>

                  {/* 진행률 */}
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    {isMasked ? (
                      <span className="text-gray-500">****</span>
                    ) : (
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div
                          className={`h-2.5 rounded-full ${getProgressColor(team.achievementRate)}`}
                          style={{ width: `${Math.min(team.achievementRate * 100, 100)}%` }}
                        ></div>
                      </div>
                    )}
                  </td>

                  {/* 근무일 */}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right tabular-nums text-gray-900">
                    {isMasked ? <span className="text-gray-500">****</span> : `${team.workingDays ?? 0}일`}
                  </td>

                  {/* 일평균 매출 */}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right tabular-nums text-gray-900">
                    {isMasked ? <span className="text-gray-500">****</span> : `${(team.dailyAverageSales ?? 0).toLocaleString()}원`}
                  </td>

                  {/* 초과/미달: 부호+색 병기 */}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right tabular-nums">
                    {isMasked ? (
                      <span className="text-gray-500">****</span>
                    ) : team.exceededSales > 0 ? (
                      <span className="text-green-600 font-medium">
                        +{team.exceededSales.toLocaleString()}원
                      </span>
                    ) : team.underperformanceSales > 0 ? (
                      <span className="text-red-600 font-medium">
                        -{team.underperformanceSales.toLocaleString()}원
                      </span>
                    ) : (
                      <span className="text-gray-500">-</span>
                    )}
                  </td>

                  {/* 차감분 */}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right tabular-nums">
                    {isMasked ? (
                      <span className="text-gray-500">****</span>
                    ) : team.deductionAmount > 0 ? (
                      <span className="text-red-600 font-medium">
                        -{Math.round(team.deductionAmount).toLocaleString()}원
                      </span>
                    ) : (
                      <span className="text-gray-500">-</span>
                    )}
                  </td>

                  {/* 인센티브 산출 (인센티브 보라: 대상 표시 전용 의미색) */}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right tabular-nums">
                    {isMasked ? (
                      <span className="text-gray-500">****</span>
                    ) : team.incentiveCalculationSales > 0 ? (
                      <span className="text-purple-600 font-medium">
                        {team.incentiveCalculationSales.toLocaleString()}원
                      </span>
                    ) : (
                      <span className="text-gray-500">-</span>
                    )}
                  </td>

                  {/* 인센티브 */}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right tabular-nums">
                    {isMasked ? (
                      <span className="text-gray-500">****</span>
                    ) : (
                      <span className={team.incentiveAmount > 0 ? 'text-gray-900 font-medium' : 'text-gray-500'}>
                        {team.incentiveAmount.toLocaleString()}원
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
});

TeamPerformanceTable.displayName = 'TeamPerformanceTable';

export default TeamPerformanceTable; 